/**
 * Klientský store globálních layoutů (committed; editor draft je zvlášť).
 *
 * ⚠ **Nikdy nic nesyntetizuje.** Dřív při chybě nebo 401 tiše dosadil in-code default
 * z `defaults.ts` — a protože ten se dávno rozešel s databází, uživatel přihlášený
 * formulářem viděl celou session dashboard bez dlaždic, které v DB jsou
 * (`docs/plans/DASHBOARD-A-KAMERY.md:31-34`). Když se plocha nenačte, musí to být VIDĚT;
 * `LayoutRoot` na to má `LayoutUnavailable`.
 */

import { create } from "zustand";
import { type LayoutDoc, validateLayout } from "@globals/layouts";
import { getLayout, getLayouts } from "@client/api/layouts";

export type LayoutEntry = {
  doc: LayoutDoc;
  version: number;
  name: string;
  updatedBy: string | null;
  updatedAt: string;
  hasSeed: boolean;
  /** Řádek v DB je, ale `layoutJson` neprošel validací. Renderer musí ukázat chybu, ne prázdno. */
  invalid: boolean;
};

/**
 * `idle` — ještě se nenačítalo (nebo po odhlášení).
 * `unauthorized` — 401. **Není to porucha** a nesmí tak vypadat; odhlášená aplikace při bootu
 * dostane 401 úplně běžně a layout si dotáhne až `LoginForm` po přihlášení.
 */
export type LayoutStatus = "idle" | "loading" | "ready" | "unauthorized" | "error";

/** Prázdný dokument pro nevalidní řádek — jen aby měl renderer co držet, než ukáže chybu. */
const EMPTY: LayoutDoc = {
  schemaVersion: 1,
  root: { kind: "container", id: "root", dir: "col", gap: 12, padding: 12, align: "stretch", justify: "start", wrap: false, children: [] },
};

type LayoutStore = {
  byKey: Map<string, LayoutEntry>;
  status: LayoutStatus;
  load: () => Promise<void>;
  refetchOne: (key: string) => Promise<void>;
  /** `undefined` = plocha není načtená. Volající to MUSÍ ošetřit, ne dosadit náhradu. */
  get: (key: string) => LayoutEntry | undefined;
  setLocal: (key: string, entry: LayoutEntry) => void;
  /** Reset (logout) — ať uložené layouty nedědí další uživatel na sdíleném počítači. */
  clear: () => void;
};

type Row = {
  key: string;
  name: string;
  layoutJson: unknown;
  version: number;
  updatedBy: string | null;
  updatedAt: string;
  hasSeed: boolean;
};

const toEntry = (r: Row): LayoutEntry => {
  const v = validateLayout(r.layoutJson);
  return {
    doc: v.ok ? v.doc : EMPTY,
    invalid: !v.ok,
    version: r.version,
    name: r.name,
    updatedBy: r.updatedBy,
    updatedAt: r.updatedAt,
    hasSeed: r.hasSeed,
  };
};

/** 401 poznáme podle odpovědi; axios sem hlásí chybu s `response.status`. */
const isUnauthorized = (e: unknown): boolean =>
  typeof e === "object" && e !== null && (e as { response?: { status?: number } }).response?.status === 401;

export const useLayoutStore = create<LayoutStore>((set, get) => ({
  byKey: new Map(),
  status: "idle",
  load: async () => {
    set({ status: "loading" });
    try {
      const res = await getLayouts();
      if (res.success && res.responseObject) {
        set({ byKey: new Map(res.responseObject.map((r) => [r.key, toEntry(r)])), status: "ready" });
        return;
      }
      set({ status: "error" });
    } catch (e) {
      set({ status: isUnauthorized(e) ? "unauthorized" : "error" });
    }
  },
  refetchOne: async (key) => {
    try {
      const res = await getLayout(key);
      if (res.success && res.responseObject) {
        const next = new Map(get().byKey);
        next.set(key, toEntry(res.responseObject));
        set({ byKey: next });
      }
    } catch {
      // Jedna neúspěšná aktualizace neshazuje celkový stav — v mapě zůstane předchozí,
      // funkční verze a `layouts:changed` nebo reconnect to zkusí znovu.
    }
  },
  get: (key) => get().byKey.get(key),
  setLocal: (key, entry) => {
    const next = new Map(get().byKey);
    next.set(key, entry);
    set({ byKey: next });
  },
  clear: () => set({ byKey: new Map(), status: "idle" }),
}));
